import ks from "../ReUsableTS/kBrowserShared";
import { kHTMLEditableColType } from "./kEditableTableTypes";
import { kSearchItems } from "../ReUsableTS/kSearch";
import type { kEditableTable } from "./kEditableTable";
import type { kEditableTableRow } from "./kEditableTableRow";
import type { kEditableTableCell } from "./kEditableTableCell";
import type { kEditableColumn } from "./kEditableColumn";
import type { kEditableTableHeading } from "./kEditableTableHeading";

// Member bodies of kEditableTable moved here verbatim (this -> Self) by the size split of 2026-10-20; every kEditableTable member keeps its signature and delegates here.
export class kEditableTableSearch {
    // Feature #10: highlight the matched substrings of the global quick-filter term
    // inside read-mode cells, reusing kSearch's GetHighlightedTextMultiWord (wraps
    // matches in <span class="highlight">). Works on ANY cell — plain text, chips,
    // hyperlink anchors, geolocation summaries, people names — by wrapping matches in
    // the cell's TEXT NODES only, so icons (<img>/<svg>), anchors + their href, and
    // every other element are preserved. A cell being edited (contentEditable or a
    // mounted <input>/<select>) and RichHtml cells are left untouched. Clearing the
    // term unwraps the spans, so every pass is idempotent.
    public static _ApplyGlobalHighlight(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Term: string = ks.Nz(Self.FilterModel.GlobalTerm).trim().toLowerCase();
            const Words: string[] = Self["_SplitWords"](Term);
            if (Words.length === 0) {
                // No search — clear any existing highlights in a single sweep.
                if (Self.Table !== null) { Self["_UnwrapHighlights"](Self.Table); }
                return;
            }
            let CurrRow: kEditableTableRow;
            for (CurrRow of Self.Rows.Items) {
                let CurrCell: kEditableTableCell;
                for (CurrCell of CurrRow.Cells) {
                    Self["_HighlightCell"](CurrCell, Words);
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch._ApplyGlobalHighlight");
        }
    }

    public static _HighlightCell(Self: kEditableTable, Cell: kEditableTableCell, Words: string[]): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Td: HTMLTableCellElement = Cell.Cell;
            // An editing cell owns its DOM (contentEditable / mounted control) —
            // never inject highlight markup into it.
            if (Self["_CellHostsEditor"](Td) === true) { return; }
            // Revert any prior highlight first so a term change re-highlights whole
            // strings and does not double-wrap.
            Self["_UnwrapHighlights"](Td);
            // RichHtml cells render authored markup — leave their formatting alone.
            if (Cell.ColDef.Type === kHTMLEditableColType.RichHtml) { return; }
            if (Self["_TextContainsAnyWord"](ks.Nz(Td.textContent), Words) === false) { return; }
            Self["_HighlightTextNodes"](Td, Words);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch._HighlightCell");
        }
    }

    // Wrap the term's matched substrings in <span class="highlight"> inside every
    // text node under Root, preserving all element structure (icons, anchors, chips,
    // the geo summary span). Text nodes are snapshotted before mutation so replacing
    // one does not disturb the walk.
    // REUSE-JUSTIFIED: the text-node walk matches DhText.TextNodes (kDeck), but the kEditableTable import graph is kept free of kalmstromShared4 (kBrowserShared) while kDeck runs on it - one shared walker would tie the two graphs' error handlers together for 6 lines.
    public static _HighlightTextNodes(Self: kEditableTable, Root: HTMLElement, Words: string[]): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Nodes: Text[] = [];
            const Walker: TreeWalker = document.createTreeWalker(Root, NodeFilter.SHOW_TEXT);
            let CurrNode: Node | null = Walker.nextNode();
            while (CurrNode !== null) {
                Nodes.push(CurrNode as Text);
                CurrNode = Walker.nextNode();
            }
            let CurrText: Text;
            for (CurrText of Nodes) {
                Self["_HighlightOneTextNode"](CurrText, Words);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch._HighlightTextNodes");
        }
    }

    // Replace every <span class="highlight"> under Root with its plain text, then
    // merge the split text nodes — reverting the highlight for any cell structure.
    public static _UnwrapHighlights(Self: kEditableTable, Root: HTMLElement): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Spans: Element[] = Array.from(Root.querySelectorAll("span.highlight"));
            if (Spans.length === 0) { return; }
            let CurrSpan: Element;
            for (CurrSpan of Spans) {
                const Restored: Text = document.createTextNode(ks.Nz(CurrSpan.textContent));
                if (CurrSpan.parentNode !== null) { CurrSpan.parentNode.replaceChild(Restored, CurrSpan); }
            }
            Root.normalize();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch._UnwrapHighlights");
        }
    }

    // A cell is mid-edit when it is contentEditable or hosts a mounted control —
    // its editor owns the DOM, so highlight markup must not be injected.
    public static _CellHostsEditor(Self: kEditableTable, Td: HTMLTableCellElement): boolean {
        if (ks.KErrorMode === true) { return true; }
        try {
            if (Td.isContentEditable === true) { return true; }
            if (Td.querySelector("input, textarea, select") !== null) { return true; }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch._CellHostsEditor");
            return true;
        }
    }

    // OR gate: true when the text contains AT LEAST ONE of the term's words. Row
    // matching is AND across the whole row, but per cell we highlight whichever words
    // THIS cell contributes (GetHighlightedTextMultiWord wraps only those).
    public static _TextContainsAnyWord(Self: kEditableTable, Text: string, Words: string[]): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            const Lower: string = Text.toLowerCase();
            let CurrWord: string;
            for (CurrWord of Words) {
                if (Lower.indexOf(CurrWord) >= 0) { return true; }
            }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch._TextContainsAnyWord");
            return false;
        }
    }

    // Split the (already lower-cased) term into its non-empty whitespace words.
    public static _SplitWords(Self: kEditableTable, Term: string): string[] {
        if (ks.KErrorMode === true) { return []; }
        try {
            const Out: string[] = [];
            let CurrWord: string;
            for (CurrWord of Term.split(" ")) {
                if (CurrWord !== "") { Out.push(CurrWord); }
            }
            return Out;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch._SplitWords");
            return [];
        }
    }

    public static _GetHighlighter(Self: kEditableTable): kSearchItems | null {
        if (ks.KErrorMode === true) { return null; }
        try {
            Self["_Highlighter"] = kSearchItems.EnsureHighlighter(Self["_Highlighter"], "kEditableTable._GetHighlighter");
            return Self["_Highlighter"];
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch._GetHighlighter");
            return null;
        }
    }

    public static _UpdateNoMatchRow(Self: kEditableTable, Visible: number): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Visible > 0 || Self.FilterModel.HasActiveFilters() === false) {
                if (Self.NoMatchRow !== null) { Self.NoMatchRow.style.display = "none"; }
                return;
            }
            if (Self.NoMatchRow === null) { Self["_BuildNoMatchRow"](); }
            if (Self.NoMatchRow !== null) { Self.NoMatchRow.style.display = "table-row"; }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch._UpdateNoMatchRow");
        }
    }

    public static _BuildNoMatchRow(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Row: HTMLTableRowElement = document.createElement("tr");
            Row.className = "kEditableTableNoMatch";
            const Cell: HTMLTableCellElement = document.createElement("td");
            Cell.colSpan = Self.Columns.Items.length + 1;
            Cell.textContent = Self.NoMatchText;
            Cell.style.textAlign = "center";
            Cell.style.padding = "16px";
            Cell.style.color = "#6b6b6b";
            Cell.style.fontStyle = "italic";
            Row.appendChild(Cell);
            Self.Table.appendChild(Row);
            Self.NoMatchRow = Row;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch._BuildNoMatchRow");
        }
    }

    public static ClearColumnFilter(Self: kEditableTable, PropName: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.FilterModel.ClearColumn(PropName);
            Self.ApplyFilters();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch.ClearColumnFilter");
        }
    }

    public static ClearFilters(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_ResetFilters"](() => { Self.FilterModel.ClearAll(); }, () => "", "kEditableTable.ClearFilters");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch.ClearFilters");
        }
    }

    // Persist / restore the whole filter state (feature #16). The host stores
    // the returned string per user/view and restores it later.
    public static GetFilterState(Self: kEditableTable): string {
        if (ks.KErrorMode === true) { return ""; }
        try {
            return Self.FilterModel.Serialize();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch.GetFilterState");
            return "";
        }
    }

    public static SetFilterState(Self: kEditableTable, Json: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_ResetFilters"](() => { Self.FilterModel.Deserialize(Json); },
                () => Self.FilterModel.GlobalTerm, "kEditableTable.SetFilterState");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch.SetFilterState");
        }
    }

    /** Moved from kEditableTable._ResetFilters (size split); the class member delegates here. */
    public static _ResetFilters(Self: kEditableTable, Model: () => void, Term: () => string, Label: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.CancelPendingFilter();
            Model();
            if (Self.FilterInput !== null) { Self.FilterInput.value = Term(); }
            Self.ApplyFilters();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch._ResetFilters");
        }
    }

    public static _ColumnByProp(Self: kEditableTable, PropName: string): kEditableColumn | null {
        if (ks.KErrorMode === true) { return null; }
        try {
            let CurrCol: kEditableColumn;
            for (CurrCol of Self.Columns.Items) {
                if (CurrCol.KObjectPropertyName === PropName) { return CurrCol; }
            }
            return null;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch._ColumnByProp");
            return null;
        }
    }

    // Placeholder for the header funnel highlight; the full implementation lands
    // with the Tier-1 popup (slice 2). Kept here so ApplyFilters can call it
    // unconditionally now.
    public static RefreshFilterIndicators(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            let CurrHeading: kEditableTableHeading;
            for (CurrHeading of Self.Headers) {
                if (CurrHeading.FilterIcon === null) { continue; }
                const Column: kEditableColumn | null = Self["_ColumnByIndex"](CurrHeading.ColumnIndex);
                if (Column === null) { continue; }
                const Active: boolean = Self.FilterModel.GetColumnFilter(Column.KObjectPropertyName) !== null;
                CurrHeading.SetFilterActive(Active);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch.RefreshFilterIndicators");
        }
    }

    public static _ColumnByIndex(Self: kEditableTable, Index: number): kEditableColumn | null {
        if (ks.KErrorMode === true) { return null; }
        try {
            if (Index < 0 || Index >= Self.Columns.Items.length) { return null; }
            return Self.Columns.Items[Index];
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch._ColumnByIndex");
            return null;
        }
    }

    public static AppendRowCountSpan(Self: kEditableTable, Footer: HTMLDivElement): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Span: HTMLSpanElement = document.createElement("span");
            Span.id = Self.IdPrefix + "-rowcount";
            Span.className = "kEditableTableRowCount";
            Footer.appendChild(Span);
            Self.RowCountSpan = Span;
            Self.UpdateRowCount(Self.Rows.Items.length);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch.AppendRowCountSpan");
        }
    }

    public static UpdateRowCount(Self: kEditableTable, Count: number): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Span: HTMLSpanElement | null = Self.RowCountSpan;
            if (Span === null) { return; }
            Span.textContent = Count.toString() + " rows";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableSearch.UpdateRowCount");
        }
    }
}
